import { Children, type ReactNode, useMemo } from 'react';
import { clsx } from 'clsx';
import { match, P } from 'ts-pattern';
import { FormHint, type FormHintVariant } from '@interxfi/design-system/components/form/hint';
import { VerticalStack, type VerticalStackProps } from '../../../alignment/vertical-stack';
import { Typography, type TypographyProps } from '../../../typography';
import { AggregateErrorContextProvider, useAggregateErrorContext } from '../../aggregate-error-context';
import inputStyles from '../../input.module.scss';
import styles from './switch-input-group.module.scss';

const defaultLabelProps: TypographyProps = {
  size: '0.75rem',
  lineHeight: '1.5rem',
  tone: 'md',
  weight: 'bold',
  transform: 'uppercase',
  letterSpacing: '1px',
};

export interface SwitchInputGroupProps extends Omit<VerticalStackProps, 'label'> {
  children: ReactNode; // must contain switch inputs
  hint?: ReactNode;
  hintVariant?: FormHintVariant;
  label?: ReactNode;
  labelProps?: TypographyProps;
}

function SwitchInputGroupInner({
  children,
  className,
  gap = 4,
  hint,
  hintVariant,
  label,
  labelProps = defaultLabelProps,
  ...props
}: SwitchInputGroupProps) {
  const switches = useMemo(() => Children.toArray(children).reduce<number>((acc, curr) => acc + (curr ? 1 : 0), 0), [children]);
  const { aggregateError } = useAggregateErrorContext();

  return (
    <VerticalStack className={clsx(inputStyles.input, styles.switchInputGroup, className)} gap={gap} {...props}>
      {label ? <Typography {...labelProps}>{label}</Typography> : null}

      <VerticalStack className={styles.container}>
        {Children.map(children, (child, index) =>
          child ? (
            <div
              className={clsx(
                styles.inputContainer,
                styles.switch,
                hint && styles.hasHint,
                aggregateError && styles.hasAggregateError,
                styles[
                  match(index)
                    .with(0, () => (switches === 1 ? 'only' : 'first'))
                    .with(switches - 1, () => 'last')
                    .otherwise(() => 'middle')
                ],
              )}
              key={match(child)
                .with({ props: { key: P.string.select() } }, (k) => k)
                .with({ props: { id: P.string.select() } }, (id) => id)
                .with({ props: { name: P.string.select() } }, (name) => name)
                .otherwise(() => index)}
            >
              {child}
            </div>
          ) : null,
        )}

        {hint ? (
          <FormHint className={clsx(styles.hint, styles.inputContainer)} size="small" variant={hintVariant}>
            {hint}
          </FormHint>
        ) : null}

        {aggregateError ? (
          <FormHint className={clsx(styles.error, styles.inputContainer)} size="small" variant="error">
            {aggregateError}
          </FormHint>
        ) : null}
      </VerticalStack>
    </VerticalStack>
  );
}

export function SwitchInputGroup(props: SwitchInputGroupProps) {
  return (
    <AggregateErrorContextProvider>
      <SwitchInputGroupInner {...props} />
    </AggregateErrorContextProvider>
  );
}
